import React, { useState, useEffect } from 'react'
import { Row, Col, Spinner } from 'react-bootstrap'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  FaSearch,
  FaFileInvoice,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaTruck,
  FaDownload,
  FaEye,
  FaCheckCircle,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
} from 'react-icons/fa'
import { fetchBilties, setFilters, clearError, downloadBiltyPDF } from '../../store/slices/biltySlice'
import './Bilty.scss'

const fade = {
  hidden: { opacity: 0, y: 12 },
  visible: i => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.05, duration: 0.32, ease: 'easeOut' }
  })
}

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'true', label: 'Approved' },
  { key: 'false', label: 'Pending' },
]

const BiltyList = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')

  const { bilties, loading, error, pagination, filters, pdfDownloading } = useSelector(state => state.bilty)

  useEffect(() => {
    dispatch(fetchBilties(filters))
  }, [dispatch, filters])

  const handleSearch = () => {
    dispatch(setFilters({ truck_number: searchQuery, page: 1 }))
  }

  const handleTabChange = tab => {
    setActiveTab(tab)
    dispatch(setFilters({ transporter_approved: tab, page: 1 }))
  }

  const handleTypeChange = val => {
    setTypeFilter(val)
    dispatch(setFilters({ bilty_type: val, page: 1 }))
  }

  const handleSourceChange = val => {
    setSourceFilter(val)
    dispatch(setFilters({ bilty_source: val, page: 1 }))
  }

  const handlePageChange = page => {
    dispatch(setFilters({ page }))
  }

  const handleDownloadPDF = (e, biltyId) => {
    e.stopPropagation()
    dispatch(downloadBiltyPDF(biltyId))
  }

  const formatDate = dateString => {
    if (!dateString) return '—'
    return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const getStatusVariant = bilty => {
    if (bilty.transporter_approved) return 'green'
    if (bilty.bilty_type === 'online') return 'yellow'
    return 'gray'
  }

  const getStatusLabel = bilty => {
    if (bilty.transporter_approved) return 'Approved'
    if (bilty.bilty_type === 'online') return 'Pending'
    return 'Manual'
  }

  const total      = pagination?.total || bilties.length
  const approved   = bilties.filter(b => b.transporter_approved).length
  const pending    = bilties.filter(b => !b.transporter_approved && b.bilty_type === 'online').length
  const manual     = bilties.filter(b => b.bilty_type === 'manual').length

  return (
    <AnimatePresence>
      <motion.div
        className="bl-page"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
      >
        {/* ── HEADER ───────────────────────────────────────────────────── */}
        <motion.div className="bl-header" custom={0} variants={fade}>
          <div className="bl-header-left">
            <div className="bl-header-icon"><FaFileInvoice /></div>
            <div>
              <h1 className="bl-title">Digital Bilty (LR)</h1>
              <p className="bl-sub">Manage and track your digital Lorry Receipts</p>
            </div>
          </div>
        </motion.div>

        {/* ── KPI CARDS ────────────────────────────────────────────────── */}
        <motion.div custom={1} variants={fade}>
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <div className="bl-kpi bl-kpi--blue">
                <div className="bl-kpi-left">
                  <span className="bl-kpi-val">{total}</span>
                  <span className="bl-kpi-label">Total Bilties</span>
                </div>
                <FaFileInvoice className="bl-kpi-icon" />
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="bl-kpi bl-kpi--green">
                <div className="bl-kpi-left">
                  <span className="bl-kpi-val">{approved}</span>
                  <span className="bl-kpi-label">Approved</span>
                </div>
                <FaCheckCircle className="bl-kpi-icon" />
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="bl-kpi bl-kpi--yellow">
                <div className="bl-kpi-left">
                  <span className="bl-kpi-val">{pending}</span>
                  <span className="bl-kpi-label">Pending</span>
                </div>
                <FaClock className="bl-kpi-icon" />
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="bl-kpi bl-kpi--purple">
                <div className="bl-kpi-left">
                  <span className="bl-kpi-val">{manual}</span>
                  <span className="bl-kpi-label">Manual</span>
                </div>
                <FaTruck className="bl-kpi-icon" />
              </div>
            </Col>
          </Row>
        </motion.div>

        {/* ── FILTER BAR ───────────────────────────────────────────────── */}
        <motion.div className="bl-filter-bar" custom={2} variants={fade}>
          <div className="bl-search-wrap">
            <FaSearch className="bl-search-icon" />
            <input
              className="bl-search"
              type="text"
              placeholder="Search by bilty number or truck…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>

          <div className="bl-select-wrap">
            <select
              className="bl-select"
              value={typeFilter}
              onChange={e => handleTypeChange(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="manual">Manual</option>
              <option value="online">Online</option>
            </select>
          </div>

          <div className="bl-select-wrap">
            <select
              className="bl-select"
              value={sourceFilter}
              onChange={e => handleSourceChange(e.target.value)}
            >
              <option value="">All Sources</option>
              <option value="main_portal">Main Portal</option>
              <option value="transporter_portal">Transporter Portal</option>
            </select>
          </div>

          <div className="bl-filter-tabs">
            {STATUS_TABS.map(t => (
              <button
                key={t.key}
                className={`bl-tab${activeTab === t.key ? ' bl-tab--active' : ''}`}
                onClick={() => handleTabChange(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <span className="bl-count">{total} record{total !== 1 ? 's' : ''}</span>
        </motion.div>

        {/* ── ERROR ────────────────────────────────────────────────────── */}
        {error && (
          <div className="bd-alert bd-alert--danger mb-3">
            {error}
            <button
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
              onClick={() => dispatch(clearError())}
            >✕</button>
          </div>
        )}

        {/* ── CONTENT ──────────────────────────────────────────────────── */}
        {loading ? (
          <div className="bl-loading">
            <Spinner animation="border" size="sm" style={{ color: 'rgb(20,35,115)' }} />
            <span>Loading bilties…</span>
          </div>
        ) : bilties.length === 0 ? (
          <div className="bl-empty">
            <div className="bl-empty-icon"><FaFileInvoice /></div>
            <p className="bl-empty-title">No Bilties Found</p>
            <p className="bl-empty-sub">Try adjusting your filters or search criteria</p>
          </div>
        ) : (
          <>
            <Row className="g-3">
              {bilties.map((bilty, i) => (
                <Col xs={12} sm={6} md={4} xl={3} key={bilty.id}>
                  <motion.div custom={i} variants={fade}>
                    <div className="bl-card">
                      <div className="bl-card-accent" />
                      <div className="bl-card-head">
                        <span className="bl-card-number">
                          <FaFileInvoice style={{ opacity: 0.6, fontSize: '0.78rem' }} />
                          {bilty.bilty_number}
                        </span>
                        <span className={`bl-badge bl-badge--${getStatusVariant(bilty)}`}>
                          {getStatusLabel(bilty)}
                        </span>
                      </div>

                      <div className="bl-route">
                        <div className="bl-route-row">
                          <FaMapMarkerAlt className="bl-route-dot bl-route-dot--from" />
                          <span className="bl-route-place">{bilty.origin || '—'}</span>
                        </div>
                        <div className="bl-route-arrow">│</div>
                        <div className="bl-route-row">
                          <FaMapMarkerAlt className="bl-route-dot bl-route-dot--to" />
                          <span className="bl-route-place">{bilty.destination || '—'}</span>
                        </div>
                      </div>

                      <div className="bl-card-details">
                        <div className="bl-card-row">
                          <FaTruck className="bl-card-icon" />
                          <span className="bl-card-val">{bilty.truck_number || '—'}</span>
                        </div>
                        <div className="bl-card-row">
                          <FaCalendarAlt className="bl-card-icon" />
                          <span className="bl-card-val">{formatDate(bilty.bilty_date)}</span>
                        </div>
                        {bilty.consignor_name && (
                          <div className="bl-card-row">
                            <FaFilter className="bl-card-icon" />
                            <span className="bl-card-val">
                              <span style={{ color: '#718096' }}>From: </span>{bilty.consignor_name}
                            </span>
                          </div>
                        )}
                        {bilty.consignee_name && (
                          <div className="bl-card-row">
                            <FaFilter className="bl-card-icon" />
                            <span className="bl-card-val">
                              <span style={{ color: '#718096' }}>To: </span>{bilty.consignee_name}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="bl-card-footer">
                        <div className="bl-card-tags">
                          <span className="bl-badge bl-badge--blue">{bilty.bilty_type}</span>
                          {bilty.bilty_source && (
                            <span className="bl-badge bl-badge--gray">
                              {bilty.bilty_source === 'main_portal' ? 'Main' : 'Transporter'}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            className="bl-card-btn bl-card-btn--view"
                            onClick={() => navigate(`/bilty/${bilty.id}`)}
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                          <button
                            className="bl-card-btn bl-card-btn--dl"
                            onClick={e => handleDownloadPDF(e, bilty.id)}
                            disabled={pdfDownloading || !bilty.pdf_url}
                            title={bilty.pdf_url ? 'Download PDF' : 'PDF not generated'}
                          >
                            <FaDownload />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </Col>
              ))}
            </Row>

            {/* ── PAGINATION ─────────────────────────────────────────── */}
            {pagination?.totalPages > 1 && (
              <div className="bl-pagination">
                <button
                  className="bl-page-btn"
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                >
                  <FaChevronLeft />
                </button>
                <span className="bl-page-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  className="bl-page-btn"
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </motion.div>
    </AnimatePresence>
  )
}

export default BiltyList
